'use client';

import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/navigation';
import { useQueryState } from 'nuqs';
import { MouseEvent, useCallback, useState } from 'react';
import { useSendParticipation } from 'src/api/connect';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { useResponsive } from 'src/hooks/use-responsive';
import { useTranslate } from 'src/locales';
import { bgGradient } from 'src/theme/css';

import { EventParticipantStatus, EventType, IEvent } from '../../types';
import { getPictureToDisplay } from '../../utils/picture';
import EventStatus from './event-status';

type EventDetailsHeaderProps = {
  event: IEvent;
};

export default function EventDetailsHeader({ event }: EventDetailsHeaderProps) {
  const {
    pictures,
    eventParticipantStatus,
    id,
    endDate,
    participants,
    eventParticipantsCount,
    cover,
    date,
  } = event;
  const [going, setGoing] = useState(eventParticipantStatus === EventParticipantStatus.GOING);
  const { enqueueSnackbar } = useSnackbar();
  const { authenticated, user } = useAuthContext();
  const { t } = useTranslate('common');
  const { t: tEvent } = useTranslate('event'); // Hook to handle translations
  const [_, setOpenLogin] = useQueryState('login');
  const [___, setIsExclusiveEvent] = useQueryState('exclusive');
  const [____, setReturnTo] = useQueryState('returnTo');

  const hasEventExpired = new Date(event?.endDate ?? date) < new Date();
  const theme = useTheme();
  const router = useRouter();
  const { sendParticipation } = useSendParticipation();
  const mdUp = useResponsive('up', 'md');
  const eventDate = new Date(endDate ?? '');
  const handleClickOnConnect = useCallback(() => {
    if (!authenticated) {
      setOpenLogin(`open`);
      setReturnTo(`https://trybe.fm/event/${id}/attendees`);
      return null;
    }
    if (event?.type === EventType.EXCLUSIVE && isEmpty(event?.tickets) && !(user?.id === 1188)) {
      setIsExclusiveEvent('open');
      return null;
    }
    sendParticipation(id?.toString(), { connect: true });
    router.push(`attendees`);

    return null;
  }, [
    authenticated,
    event?.type,
    setReturnTo,
    event?.tickets,
    user?.id,
    sendParticipation,
    id,
    router,
    setOpenLogin,
    setIsExclusiveEvent,
  ]);

  const onChangeStatus = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!authenticated) {
        setOpenLogin('open');
      } else if (event?.type === EventType.EXCLUSIVE && isEmpty(event?.tickets)) {
        setIsExclusiveEvent('open');
      } else {
        await sendParticipation(`${id}`, {
          status: (going ? 'CANCELLED' : 'GOING') as EventParticipantStatus,
          connect: true,
        });
        await setGoing((prev) => !prev);
        enqueueSnackbar(!going ? t('snackbar.going-message') : t('snackbar.not-going-message'), {
          variant: !going ? 'success' : 'warning',
        });
      }
    },
    [
      authenticated,
      setOpenLogin,
      event,
      setIsExclusiveEvent,
      sendParticipation,
      id,
      going,
      enqueueSnackbar,
      t,
    ]
  );

  return (
    <Stack
      flexGrow={1}
      sx={{
        ...(mdUp && {
          width: '100%',
          height: '100%',
          maxHeight: '40vh',
          position: 'relative',
          mb: 6,
          borderRadius: 2,
          boxShadow: 'rgb(0, 0, 0) 0px 1px 3px',
          ...bgGradient({
            imgUrl: cover?.url ?? pictures[0]?.url,
            startColor: `${alpha(theme.palette.grey[900], 0.3)} 0%`,
            endColor: `${alpha(theme.palette.grey[900], 0.3)} 100%`,
          }),
        }),
        ...(!mdUp && {
          position: 'relative',
          height: '40vh',
          ...bgGradient({
            imgUrl: cover?.url ?? pictures[0]?.url,
            startColor: `${alpha(theme.palette.grey[900], 0.3)} 0%`,
            endColor: `${alpha(theme.palette.grey[900], 0.3)} 100%`,
          }),
        }),
      }}
    >
      {mdUp && (
        <Stack
          sx={{
            right: 5,
            paddingX: 1,
            paddingY: 1,
            zIndex: 9,
            minWidth: 'max-content',
            display: 'flex',
            gap: 1,
            borderRadius: 1,
            top: 5,
            position: 'absolute',
          }}
        >
          <EventStatus event={event} eventDate={eventDate} />
        </Stack>
      )}
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          left: '50%',
          transform: 'translate(-50%, 0%)',
          paddingX: 1,
          paddingY: 1,
          zIndex: 9,
          minWidth: 'max-content',
          display: 'flex',
          gap: 1,
          borderRadius: 1,
          bottom: '-18px',
          backgroundColor: '#000',
          position: 'absolute',
        }}
      >
        {participants.length > 0 && (
          <>
            <AvatarGroup onClick={() => handleClickOnConnect()}>
              {participants.map((participant, index) => (
                <Avatar
                  key={index}
                  src={getPictureToDisplay(participant)}
                  sx={{ width: 30, height: 30, mr: 0.1 }}
                />
              ))}
            </AvatarGroup>
            <Typography variant="caption" sx={{ flexGrow: 1, mr: 1 }}>
              {eventParticipantsCount > participants?.length && '+'}
              {(eventParticipantsCount ?? 0) - (participants?.length ?? 0) > 0
                ? `${(eventParticipantsCount ?? 0) - (participants?.length ?? 0)} `
                : ''}
              {tEvent('event-participants.are-going')}
            </Typography>
          </>
        )}
        {!participants.length && (
          <Typography
            variant="caption"
            sx={{ flexGrow: 1, mr: 2, fontWeight: 600, fontSize: '14px', pl: 1 }}
          >
            {tEvent('event-participants.be-the-first')} 🔥
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          disabled={hasEventExpired}
          onClick={onChangeStatus}
          endIcon={
            <Iconify icon={going ? 'ant-design:heart-filled' : 'ant-design:heart-outlined'} />
          }
        >
          {tEvent('event-participants.rsvp')}
        </Button>
      </Stack>
    </Stack>
  );
}
